import {
    Box,
    Grid,
    Toolbar,
    Typography,
    FormControl,
    Select,
    MenuItem,
    CircularProgress,
    Button,
    Pagination
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../../../utils/Api";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { CloudDownloadOutlined } from "@mui/icons-material";
import jsPDF from "jspdf";
import "jspdf-autotable";

function ConsumptionReport() {
    const token = localStorage.getItem("token");
    const userRole = localStorage.getItem("role");
    const outletData = JSON.parse(localStorage.getItem("outletData") || "{}");

    const [loading, setLoading] = useState(false);

    // Filters
    const [selectedCategory, setSelectedCategory] = useState("");
    const [selectedMaterial, setSelectedMaterial] = useState("");

    // DatePicker expects Date objects
    const [selectedStartDate, setSelectedStartDate] = useState(new Date());
    const [selectedEndDate, setSelectedEndDate] = useState(new Date());

    // Dropdown categories (from current stock api - your existing api)
    const [categories, setCategories] = useState([]);

    // Report rows (use rawmaterialPreviewData from report api)
    const [reportRows, setReportRows] = useState([]);

    // pagination from API (paginationRawData)
    const [pagination, setPagination] = useState({
        totalDocuments: 0,
        limit: 50,
        NumberOfPages: 1,
        currentPage: 1,
    });

    const itemsPerPage = 50; // match API limit
    const [currentPage, setCurrentPage] = useState(1);

    // ---------- CATEGORY LIST (your existing current stock call) ----------
    const getRawMaterialsList = async () => {
        try {
            const response = await axios.get(
                `${API.BASE_URL}stock-transactions/getOutletCurrentStock`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    params: {
                        outletId: outletData?._id,
                        limit: 100,
                    },
                }
            );

            setCategories(response?.data?.data?.rawMaterialCategories || []);
        } catch (error) {
            console.error("Error:", error);
        }
    };

    const uniqueCategories = useMemo(() => {
        return (categories || []).filter(
            (category, index, self) =>
                index ===
                self.findIndex((c) => c?.mainCategory?._id === category?.mainCategory?._id)
        );
    }, [categories]);

    // ---------- REPORT API CALL ----------
    const getStockTransactions = async (page = currentPage) => {
        if (!outletData?._id) return;

        setLoading(true);
        try {
            const startDateStr = format(selectedStartDate, "yyyy-MM-dd");
            const endDateStr = format(selectedEndDate, "yyyy-MM-dd");

            const params = {
                startDate: startDateStr,
                endDate: endDateStr,
                type: "SALE", // your example used SALE
                outletId: outletData?._id,
                page,
                limit: itemsPerPage,
            };

            if (selectedCategory) params.mainCategory = selectedCategory;
            if (selectedMaterial) params.rawMaterialId = selectedMaterial;

            const response = await axios.get(
                `${API.BASE_URL}stock-transactions/getOutletStockTransactions`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    params,
                }
            );

            const preview = response?.data?.data?.rawmaterialPreviewData || [];
            const pag = response?.data?.data?.paginationRawData || {};

            setReportRows(preview);
            setPagination({
                totalDocuments: pag.totalDocuments || preview.length,
                limit: pag.limit || itemsPerPage,
                NumberOfPages: pag.NumberOfPages || 1,
                currentPage: pag.currentPage || page,
            });

            setCurrentPage(pag.currentPage || page);
        } catch (error) {
            console.error("Error:", error);
            setReportRows([]);
            toast.error("Failed to fetch report", { autoClose: 3000 });
        } finally {
            setLoading(false);
        }
    };

    // ---------- PDF EXPORT (summary reportRows) ----------
    const generatePDF = () => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();

        const title = "Stock Consumption Report (Summary)";
        const textWidth = doc.getTextWidth(title);
        const titleX = (pageWidth - textWidth) / 2;

        doc.setFontSize(16);
        doc.text(title, titleX, 20);

        doc.setFontSize(10);
        const outletName = outletData?.name || "Unknown Outlet";
        const outletAddress = outletData?.address || "No Address Available";
        const outletContact = `${outletData?.email || "No Email"}, ${outletData?.phone || "No Phone Info"}`;

        doc.text(`Outlet: ${outletName}`, 14, 30);
        doc.text(`Address: ${outletAddress}`, 14, 40);
        doc.text(`Contact: ${outletContact}`, 14, 50);

        const startDateStr = format(selectedStartDate, "yyyy-MM-dd");
        const endDateStr = format(selectedEndDate, "yyyy-MM-dd");
        doc.text(`From: ${startDateStr}  To: ${endDateStr}`, 14, 60);

        const columns = [
            { header: "#", dataKey: "itemNo" },
            { header: "Raw Material", dataKey: "rawMaterial" },
            { header: "Category", dataKey: "mainCategory" },
            { header: "Sub Category", dataKey: "subCategory" },
            { header: "HSN", dataKey: "hsn" },
            { header: "Tax %", dataKey: "taxPercent" },
            { header: "Type", dataKey: "type" },
            { header: "Qty", dataKey: "quantity" },
            { header: "Unit", dataKey: "unit" },
        ];

        const sortedRows = [...reportRows].sort((a, b) => {
            const ca = (a.mainCategory || "").toLowerCase();
            const cb = (b.mainCategory || "").toLowerCase();
            if (ca !== cb) return ca.localeCompare(cb);

            const sa = (a.subCategory || "").toLowerCase();
            const sb = (b.subCategory || "").toLowerCase();
            return sa.localeCompare(sb);
        });

        const rows = sortedRows.map((r, index) => ({
            itemNo: index + 1,
            rawMaterial: r.rawMaterial,
            mainCategory: r.mainCategory,
            subCategory: r.subCategory,
            hsn: r.hsn,
            taxPercent: r.taxPercent,
            type: r.type,
            quantity: Number(r.quantity).toFixed(3),
            unit: r.unit,
        }));

        doc.autoTable({
            columns,
            body: rows,
            startY: 70,
            theme: "grid",
            styles: { halign: "center", fontSize: 8 },
            headStyles: { halign: "center" },
        });

        const today = new Date();
        const fileDate = today.toISOString().split("T")[0];
        doc.save(`${fileDate}-Stock-Consumption-Report.pdf`);
    };

    // ---------- EVENTS ----------
    const handleCategoryChange = (event) => {
        const categoryId = event.target.value;
        setSelectedCategory(categoryId);
        setCurrentPage(1);
    };

    const handleMaterialChange = (event) => {
        setSelectedMaterial(event.target.value);
        setCurrentPage(1);
    };

    const handlePageChange = (event, page) => {
        setCurrentPage(page);
        getStockTransactions(page);
    };

    useEffect(() => {
        getRawMaterialsList();
    }, []);

    return (
        <>
            <Box className="dashBoard">
                <Toolbar />

                <Grid container justifyContent={"center"}>
                    <Grid item md={11} flexDirection={"column"}>
                        <Grid container md={12} justifyContent={"space-evenly"} sx={{ marginTop: "35px" }}>
                            <Grid item md={12}>
                                <Typography sx={{ fontSize: "16px", margin: "16px 0 30px 40px", textAlign: "center", fontWeight: 600 }}>
                                    Stock Consumption Reports
                                </Typography>
                            </Grid>

                            {/* Category */}
                            <Grid item md={2}>
                                <label style={{ paddingLeft: "10px" }}>Category</label>
                                <FormControl
                                    fullWidth
                                    variant="outlined"
                                    sx={{
                                        backgroundColor: "#fff",
                                        marginTop: "5px",
                                        marginLeft: "5px",
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "#cecece",
                                            borderWidth: "2px",
                                        },
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "10px",
                                            "&.Mui-focused fieldset": { borderColor: "#cecece" },
                                        },
                                    }}
                                    style={{ height: "56px", width: "80%" }}
                                >
                                    <Select
                                        id="category-select"
                                        value={selectedCategory}
                                        onChange={handleCategoryChange}
                                    >
                                        <MenuItem value="">
                                            <em>All</em>
                                        </MenuItem>

                                        {uniqueCategories?.map((category) => (
                                            <MenuItem key={category?.mainCategory?._id} value={category?.mainCategory?._id}>
                                                {category?.mainCategory?.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Start Date */}
                            <Grid item md={2}>
                                <label style={{ paddingLeft: "10px" }}>From Date</label>
                                <DatePicker
                                    selected={selectedStartDate}
                                    onChange={(date) => setSelectedStartDate(format(date, 'yyyy-MM-dd'))}
                                    dateFormat="yyyy-MM-dd"
                                    placeholderText="Select Date"
                                    showYearDropdown
                                    scrollableYearDropdown
                                    yearDropdownItemNumber={100}
                                    customInput={
                                        <FormControl
                                            fullWidth
                                            sx={{
                                                backgroundColor: '#fff',
                                                marginTop: '5px',
                                                marginLeft: '5px',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#cecece',
                                                    borderWidth: '1px',
                                                },
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '10px',
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#cecece',
                                                    },
                                                },
                                                '& input': {
                                                    height: '40px',
                                                },
                                            }}
                                        >
                                            <input
                                                className="form-control"
                                                placeholder='Select Start Date'
                                                value={selectedStartDate ? format(new Date(selectedStartDate), 'yyyy-MM-dd') : ''}
                                                style={{ height: '56px', borderRadius: '10px', paddingLeft: '10px' }}
                                                readOnly
                                            />
                                        </FormControl>
                                    }
                                />
                            </Grid>

                            {/* End Date */}
                            <Grid item md={2}>
                                <label style={{ paddingLeft: "10px" }}>To Date</label>
                                <DatePicker
                                    selected={selectedEndDate}
                                    onChange={(date) => setSelectedEndDate(format(date, 'yyyy-MM-dd'))}
                                    dateFormat="yyyy-MM-dd"
                                    placeholderText="Select Date"
                                    showYearDropdown
                                    scrollableYearDropdown
                                    yearDropdownItemNumber={100}
                                    customInput={
                                        <FormControl
                                            fullWidth
                                            sx={{
                                                backgroundColor: '#fff',
                                                marginTop: '5px',
                                                marginLeft: '5px',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#cecece',
                                                    borderWidth: '1px',
                                                },
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '10px',
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#cecece',
                                                    },
                                                },
                                                '& input': {
                                                    height: '40px',
                                                },
                                            }}
                                        >
                                            <input
                                                className="form-control"
                                                placeholder='Select Start Date'
                                                value={selectedEndDate ? format(new Date(selectedEndDate), 'yyyy-MM-dd') : ''}
                                                style={{ height: '56px', borderRadius: '10px', paddingLeft: '10px' }}
                                                readOnly
                                            />
                                        </FormControl>
                                    }
                                />
                            </Grid>

                            <Grid
                                item
                                md={2}
                                textAlign="center"
                                sx={{ display: "flex", alignItems: "center", justifyContent: "center", marginTop: "25px" }}
                            >
                                <Button
                                    variant="contained"
                                    onClick={() => getStockTransactions(1)}
                                    sx={{
                                        padding: "10px 20px",
                                        fontSize: "16px",
                                        fontWeight: "bold",
                                        borderRadius: "8px",
                                        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
                                        textTransform: "none",
                                        backgroundColor: "#003049",
                                        "&:hover": {
                                            backgroundColor: "darkcyan",
                                            boxShadow: "0px 6px 12px rgba(0, 0, 0, 0.3)",
                                        },
                                    }}
                                >
                                    Get Reports
                                </Button>
                            </Grid>

                            {/* Download PDF */}
                            {reportRows.length > 0 && (
                                <Grid item md={1} sx={{ display: "flex", alignItems: "center", justifyContent: "center", marginTop: "25px" }}>
                                    <CloudDownloadOutlined
                                        onClick={generatePDF}
                                        sx={{ fontSize: "45px", cursor: "pointer", color: "#003049" }}
                                    />
                                </Grid>
                            )}
                        </Grid>

                        {/* LOADER / EMPTY / TABLE */}
                        {loading ? (
                            <Box sx={{ height: "100px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <CircularProgress />
                                <Typography sx={{ paddingLeft: "10px" }}>Fetching Report</Typography>
                            </Box>
                        ) : (
                            <>
                                {reportRows?.length === 0 ? (
                                    <Typography sx={{ textAlign: "center", padding: "40px", fontSize: "16px" }}>
                                        No consumption report to display. Select filters and click "Get Reports".
                                    </Typography>
                                ) : (
                                    <Grid
                                        container
                                        md={userRole !== "CASHIER" ? 11 : 11}
                                        sx={{
                                            boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.2)",
                                            borderRadius: "8px",
                                            padding: "0px",
                                            margin: "21px 0px 25px 10px",
                                        }}
                                    >
                                        {/* Header Row */}
                                        <Grid item md={1} sx={{ padding: "20px 10px", borderRight: "1px solid grey" }}>
                                            <Typography sx={{ textAlign: "center", fontSize: "15px" }}>No.</Typography>
                                        </Grid>
                                        <Grid item md={5} sx={{ padding: "20px 0px", borderRight: "1px solid grey" }}>
                                            <Typography sx={{ textAlign: "center", fontSize: "15px" }}>Raw Material Details</Typography>
                                        </Grid>
                                        <Grid item md={2} sx={{ padding: "20px 0px", borderRight: "1px solid grey" }}>
                                            <Typography sx={{ textAlign: "center", fontSize: "15px" }}>Type</Typography>
                                        </Grid>
                                        <Grid item md={2} sx={{ padding: "20px 0px", borderRight: "1px solid grey" }}>
                                            <Typography sx={{ textAlign: "center", fontSize: "15px" }}>Quantity</Typography>
                                        </Grid>
                                        <Grid item md={2} sx={{ padding: "20px 0px" }}>
                                            <Typography sx={{ textAlign: "center", fontSize: "15px" }}>Unit</Typography>
                                        </Grid>

                                        {/* Data Rows */}
                                        {reportRows.map((row, index) => {
                                            const isEvenRow = index % 2 === 0;
                                            const backgroundColor = isEvenRow ? "#EDF9FF" : "#fff";

                                            return (
                                                <React.Fragment key={index}>
                                                    <Grid
                                                        item
                                                        md={1}
                                                        sx={{
                                                            backgroundColor,
                                                            padding: "20px 10px",
                                                            borderRight: "1px solid grey",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                        }}
                                                    >
                                                        <Typography sx={{ fontSize: "15px" }}>{index + 1}</Typography>
                                                    </Grid>

                                                    <Grid
                                                        item
                                                        md={5}
                                                        sx={{
                                                            backgroundColor,
                                                            padding: "20px 10px 20px 20px",
                                                            borderRight: "1px solid grey",
                                                        }}
                                                    >
                                                        <Typography sx={{ fontSize: "16px", fontWeight: "bold", textTransform: "capitalize" }}>
                                                            {row?.rawMaterial}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: "13px", textTransform: "capitalize" }}>
                                                            {row?.mainCategory} &gt; {row?.subCategory}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: "12px" }}>
                                                            HSN: {row?.hsn} | Tax: {row?.taxPercent}%
                                                        </Typography>
                                                        {row?.storageLocation && row?.storageLocation !== "N/A" && (
                                                            <Typography sx={{ fontSize: "12px", fontStyle: "italic" }}>
                                                                Storage: {row?.storageLocation}
                                                            </Typography>
                                                        )}
                                                    </Grid>

                                                    <Grid
                                                        item
                                                        md={2}
                                                        sx={{
                                                            backgroundColor,
                                                            padding: "20px 10px",
                                                            borderRight: "1px solid grey",
                                                            display: "flex",
                                                            justifyContent: "center",
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        <Typography sx={{ fontSize: "15px" }}>{row?.type}</Typography>
                                                    </Grid>

                                                    <Grid
                                                        item
                                                        md={2}
                                                        sx={{
                                                            backgroundColor,
                                                            padding: "20px 10px",
                                                            borderRight: "1px solid grey",
                                                            display: "flex",
                                                            justifyContent: "center",
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        <Typography sx={{ fontSize: "15px" }}>
                                                            {Number(row?.quantity || 0).toFixed(3)}
                                                        </Typography>
                                                    </Grid>

                                                    <Grid
                                                        item
                                                        md={2}
                                                        sx={{
                                                            backgroundColor,
                                                            padding: "20px 10px",
                                                            display: "flex",
                                                            justifyContent: "center",
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        <Typography sx={{ fontSize: "15px" }}>{row?.unit}</Typography>
                                                    </Grid>
                                                </React.Fragment>
                                            );
                                        })}
                                    </Grid>
                                )}

                                {/* Pagination */}
                                {reportRows.length > 0 && (
                                    <Grid container justifyContent="center" sx={{ padding: "20px 0" }}>
                                        <Pagination
                                            count={pagination?.NumberOfPages || 1}
                                            page={currentPage}
                                            onChange={handlePageChange}
                                            color="primary"
                                            variant="outlined"
                                            shape="rounded"
                                        />
                                    </Grid>
                                )}
                            </>
                        )}
                    </Grid>
                </Grid>
            </Box>
        </>
    );
}

export default ConsumptionReport;
